import { supabase } from '@/services/supabase';
import type {
  PartnerDiagnosticCode,
  PartnerDiagnosticPhase,
  PartnerPendingDiagnostic,
} from '@/utils/partnerChecklistDraft';

export type PartnerPermission = 'hidden' | 'read' | 'edit';
export type PartnerCompany = { id?: string; nome?: string | null; cnpj?: string | null; telefone?: string | null;
  email?: string | null; endereco?: string | null; cidade?: string | null; uf?: string | null; cep?: string | null; logo_url?: string | null };

export type PartnerChecklistField = {
  id: string;
  nome: string;
  tipo: string;
  grupo: string;
  ordem: number;
  obrigatorio: boolean;
  permission?: PartnerPermission;
  editable?: boolean;
  original_campo_id?: string | null;
  snapshot_field_id?: string | null;
  background_url?: string | null;
};

export type PartnerChecklistState = {
  mode: 'partner';
  revision: number;
  allow_complete: boolean;
  share_link: {
    id: string;
    status: string;
    expires_at: string;
  };
  checklist: {
    id?: string;
    codigo?: string | null;
    status?: string | null;
    nome?: string | null;
    template_id?: string;
    sequencial_codigo?: string | null;
    sequencial_numero?: number | null;
    data_criacao?: string | null;
    data_conclusao?: string | null;
  };
  empresa?: PartnerCompany | null;
  receipt?: boolean;
  fields: PartnerChecklistField[];
  values: Record<string, string | null>;
  observations?: Record<string, string | null>;
  storage_refs?: Record<string, string | null>;
  dropdown_options?: Record<string, string[]>;
};

export type PartnerChecklistReceipt = PartnerChecklistState & {
  receipt: true;
  receipt_created_at: string;
  media_metadata: Record<string, { created_at?: string | null; updated_at?: string | null }>;
};

export type PartnerActionResult = { success?: true; revision: number; [key: string]: unknown };

export type PartnerSessionCloseResult = PartnerActionResult & {
  session_status: 'completed';
  checklist_status: 'in_progress' | 'done';
};

export type PartnerMediaPreparation = PartnerActionResult & {
  uploadId: string;
  bucket: 'checklist-partner-media';
  path: string;
  mimeType: string;
  upload: { signedUrl: string; token: string; path: string };
};

export class PartnerChecklistError extends Error {
  readonly code: string;
  readonly protocol: string | null;
  readonly status: number | null;

  constructor(message: string, code: string, protocol: string | null = null, status: number | null = null) {
    super(message);
    this.name = 'PartnerChecklistError';
    this.code = code;
    this.protocol = protocol;
    this.status = status;
  }
}

type PartnerPayload = Record<string, unknown> & {
  success?: boolean;
  error?: string;
  code?: string;
  protocol?: string;
};

const responseBody = async (error: unknown): Promise<PartnerPayload | null> => {
  if (!error || typeof error !== 'object') return null;
  const context = (error as { context?: unknown }).context;
  if (context && typeof context === 'object' && typeof (context as { clone?: unknown }).clone === 'function') {
    try {
      const response = (context as Response).clone();
      const body = await response.json();
      return body && typeof body === 'object' ? body as PartnerPayload : null;
    } catch {
      return null;
    }
  }
  return null;
};

const invokePartnerResponse = async <T extends PartnerPayload>(body: Record<string, unknown>, signal: AbortSignal): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('checklist-partner', { body, signal });
  let payload = data as PartnerPayload | null;
  if (error) {
    payload = (await responseBody(error)) ?? payload;
    throw new PartnerChecklistError(
      typeof payload?.error === 'string' ? payload.error : 'Falha na solicitação do parceiro.',
      typeof payload?.code === 'string' ? payload.code : 'request_failed',
      typeof payload?.protocol === 'string' ? payload.protocol : null,
      typeof (error as { context?: { status?: unknown } }).context?.status === 'number'
        ? (error as { context: { status: number } }).context.status
        : null
    );
  }
  if (!payload || payload.success === false) {
    throw new PartnerChecklistError(
      typeof payload?.error === 'string' ? payload.error : 'Falha na solicitação do parceiro.',
      typeof payload?.code === 'string' ? payload.code : 'request_failed',
      typeof payload?.protocol === 'string' ? payload.protocol : null
    );
  }
  return payload as T;
};

const invokePartner = async <T extends PartnerPayload>(body: Record<string, unknown>): Promise<T> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new PartnerChecklistError('A conexão demorou para responder.', 'network_error'));
      controller.abort();
    }, 20000);
  });
  try {
    return await Promise.race([invokePartnerResponse<T>(body, controller.signal), deadline]);
  } finally { clearTimeout(timer); }
};

export const resolvePartnerChecklist = (token: string): Promise<PartnerChecklistState> =>
  invokePartner<PartnerChecklistState & PartnerPayload>({ action: 'resolve', token });

export const resolvePartnerReceipt = (token: string): Promise<PartnerChecklistReceipt> =>
  invokePartner<PartnerChecklistReceipt & PartnerPayload>({ action: 'receipt', token });

export const patchPartnerChecklist = (
  token: string,
  changes: Record<string, string | null>,
  observations: Record<string, string | null>,
  requestId: string,
  expectedRevision: number
): Promise<PartnerActionResult> => invokePartner<PartnerActionResult & PartnerPayload>({
  action: 'patch', token, changes, observations, requestId, expectedRevision,
});

const closePartnerChecklistSession = (
  token: string,
  finalizeChecklist: boolean,
  requestId: string,
  expectedRevision: number
): Promise<PartnerSessionCloseResult & PartnerPayload> => invokePartner<PartnerSessionCloseResult & PartnerPayload>({
  action: 'complete', token, finalizeChecklist, requestId, expectedRevision,
});

export const submitPartnerChecklist = (
  token: string,
  requestId: string,
  expectedRevision: number
): Promise<PartnerSessionCloseResult> => closePartnerChecklistSession(token, false, requestId, expectedRevision);

export const completePartnerChecklist = (
  token: string,
  requestId: string,
  expectedRevision: number
): Promise<PartnerSessionCloseResult> => closePartnerChecklistSession(token, true, requestId, expectedRevision);

export const preparePartnerMedia = (
  token: string,
  fieldId: string,
  mimeType: string,
  requestId: string,
  expectedRevision: number
): Promise<PartnerMediaPreparation> => invokePartner<PartnerMediaPreparation & PartnerPayload>({
  action: 'prepare_media', token, fieldId, mimeType, requestId, expectedRevision,
});

export const uploadPartnerMedia = async (
  prepared: PartnerMediaPreparation,
  blob: Blob,
  mimeType: string
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new PartnerChecklistError(
      'Ainda não foi possível confirmar o envio da mídia.', 'network_error',
    )), 60000);
  });
  let result;
  try {
    // O SDK não aceita abort neste método; um envio tardio mantém o mesmo caminho e o retry confirma o objeto existente.
    result = await Promise.race([supabase.storage.from(prepared.bucket)
      .uploadToSignedUrl(prepared.path, prepared.upload.token, blob, { contentType: mimeType }), deadline]);
  } finally { clearTimeout(timer); }
  const { error } = result;
  if (!error) return;
  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  const alreadyExists = error.statusCode === '409' || message.includes('already exists') || message.includes('duplicate');
  throw new PartnerChecklistError(
    alreadyExists ? 'A mídia já foi enviada e será confirmada.' : 'Falha ao enviar a mídia.',
    alreadyExists ? 'partner_upload_already_exists' : 'partner_upload_failed',
    null,
    Number(error.statusCode) || null
  );
};

export const confirmPartnerMedia = (
  token: string,
  uploadId: string,
  requestId: string,
  expectedRevision: number
): Promise<PartnerActionResult> => invokePartner<PartnerActionResult & PartnerPayload>({
  action: 'confirm_media', token, uploadId, requestId, expectedRevision,
});

export const submitPartnerDiagnostic = async (
  token: string,
  diagnostic: PartnerPendingDiagnostic
): Promise<'sent' | 'limited' | 'pending'> => {
  try {
    const { data, error } = await supabase.functions.invoke('checklist-partner', { body: {
      action: 'diagnostic',
      token,
      requestId: diagnostic.requestId,
      phase: diagnostic.phase satisfies PartnerDiagnosticPhase,
      code: diagnostic.code satisfies PartnerDiagnosticCode,
      build: diagnostic.build,
      online: diagnostic.online,
      attempt: Math.min(10, Math.max(1, diagnostic.attempt)),
    } });
    if (error || !data || typeof data !== 'object') return 'pending';
    if ((data as PartnerPayload).limited === true) return 'limited';
    return (data as PartnerPayload).success === true ? 'sent' : 'pending';
  } catch {
    return 'pending';
  }
};

export const isPartnerUploadAlreadyExists = (error: unknown): boolean =>
  error instanceof PartnerChecklistError && error.code === 'partner_upload_already_exists';

export const classifyPartnerDiagnosticCode = (error: unknown): PartnerDiagnosticCode => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'network_error';
  if (error instanceof PartnerChecklistError) {
    const code = error.code.toLowerCase();
    if (code === 'network_error') return 'network_error';
    if (code.includes('inactive') || code.includes('expired') || code.includes('revoked')) return 'link_inactive';
    if (code.includes('forbidden') || code.includes('permission') || code.includes('denied')) return 'forbidden';
    if (code.includes('conflict') || code.includes('revision')) return 'conflict';
    if (code.includes('required')) return 'required_pending';
    if (code.includes('media') || code.includes('upload')) return 'media_invalid';
    if (code.includes('validation') || code.includes('invalid')) return 'validation_error';
    return 'request_failed';
  }
  if (error instanceof TypeError || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return 'network_error';
  }
  return 'unexpected_error';
};

export const isPartnerConflict = (error: unknown): boolean =>
  error instanceof PartnerChecklistError && /conflict|revision/i.test(error.code);

export const isPartnerForbidden = (error: unknown): boolean =>
  error instanceof PartnerChecklistError && /forbidden|permission|denied|not_editable/i.test(error.code);

export const isPartnerInactive = (error: unknown): boolean =>
  error instanceof PartnerChecklistError && /inactive|expired|revoked|completed/i.test(error.code);

export const isPartnerCompleted = (error: unknown): boolean =>
  error instanceof PartnerChecklistError && error.code === 'partner_link_completed';

export const isPartnerRequiredPending = (error: unknown): boolean =>
  error instanceof PartnerChecklistError && error.status === 400 && error.code === 'partner_required_pending';

export const getPartnerErrorProtocol = (error: unknown): string | null =>
  error instanceof PartnerChecklistError ? error.protocol : null;

export const getPartnerErrorMessage = (error: unknown): string => {
  if (!(error instanceof PartnerChecklistError)) return 'Falha de conexão. Tente novamente.';
  const code = error.code.toLowerCase();
  if (code === 'partner_receipt_unavailable') return 'Comprovante indisponível para este link.';
  if (code === 'partner_feature_disabled') return 'O acesso de parceiros não está habilitado para esta empresa.';
  if (code === 'network_error') return 'A conexão demorou para responder. Seu rascunho foi mantido. Tente novamente.';
  if (/inactive|expired|revoked/.test(code)) return 'Este link foi encerrado ou expirou. Solicite um novo acesso ao responsável.';
  if (/forbidden|permission/.test(code)) return 'Este campo não está autorizado para alteração neste acesso.';
  if (/conflict|revision/.test(code)) return 'Os dados mudaram em outra sessão. Atualizamos o link; revise e tente novamente.';
  if (/required/.test(code)) return 'Há dados obrigatórios pendentes. Envie o preenchimento e peça à empresa para completar os dados ou ajustar as permissões.';
  if (/media_size/.test(code)) return 'A imagem excede o limite permitido. Escolha uma imagem menor.';
  if (/media_type/.test(code)) return 'Esse formato de imagem não é aceito.';
  if (/upload_not_ready/.test(code)) return 'O envio da imagem ainda não terminou. Tente novamente.';
  if (/validation|invalid/.test(code)) return 'Confira os dados e tente novamente.';
  return 'Não foi possível concluir a operação. Tente novamente.';
};
